import { captureException } from '@sentry/react';
import { saveAs } from 'file-saver';

import { env } from '~/constants/env';

import {
  FetchError,
  HttpBusinessConflictError,
  HttpClientError,
  httpErrorFromStatus,
  isAbortError,
  isFetchError,
  isTimeoutError,
  shouldCaptureErrorInSentry,
} from './errors';

type FetchOptions = {
  body?: unknown;
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  responseType?: 'json' | 'blob';
  version?: 'v1' | 'v2';
};

export const request = async (path: `/${string}`, options: FetchOptions = {}): Promise<unknown> => {
  try {
    const method = options.method ?? 'GET';
    const headers = {
      Accept: 'application/json',
      ...(!(options.body instanceof FormData) && { 'Content-Type': 'application/json' }),
    };
    const body = options.body instanceof FormData ? options.body : JSON.stringify(options.body ?? null);

    const response = await fetch(`${env.API_URL}/api/${options.version ?? 'v1'}${path}`, {
      method,
      headers,
      credentials: 'include',
      ...(method !== 'GET' && { body }),
      signal: AbortSignal.timeout(30_000),
    });

    if (!response.ok) await throwErrorResponse(response);

    if (response.status === 204) return null;

    return options.responseType === 'blob' ? response.blob() : response.json();
  } catch (error) {
    throwFetchError(error, path);
  }
};

export const download = async (path: `/${string}`, options: Omit<FetchOptions, 'responseType'>): Promise<void> => {
  try {
    const method = options.method ?? 'GET';
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };
    const body = JSON.stringify(options.body ?? null);

    const response = await fetch(`${env.API_URL}/api/${options.version ?? 'v1'}${path}`, {
      method,
      headers,
      credentials: 'include',
      ...(method !== 'GET' && { body }),
    });

    if (!response.ok) await throwErrorResponse(response);

    if (response.status === 204) return;

    const blob = await response.blob();

    if (blob.size === 0) return;

    const contentDispositionHeader = response.headers.get('Content-Disposition') ?? '';
    const fileNameUtf8Regex = /filename\*=UTF-8''(?<name>[^;]+);?/i;
    const fileNameRegex = /filename="?(?<name>[^;"]+)"?;?/i;
    const fileName =
      contentDispositionHeader.match(fileNameUtf8Regex)?.groups?.name.trim() ||
      contentDispositionHeader.match(fileNameRegex)?.groups?.name.trim() ||
      'download';

    saveAs(blob, decodeURIComponent(fileName));
  } catch (error) {
    throwFetchError(error, path);
  }
};

// Helper to transform errors to the corresponding HttpError class.
const throwErrorResponse = async (response: Response) => {
  if (response.ok) return;

  const statusError = httpErrorFromStatus(response.status);

  if (statusError) throw statusError;

  const error = await response.json();

  if (response.status === 409) throw new HttpBusinessConflictError(error.errorCode);

  throw new HttpClientError(response.status, { cause: error });
};

// Helper to transform errors to the corresponding FetchError class, or rethrow if not a FetchError.
// Mirrors `fetch.ts`'s `onError`, including reporting to Sentry, which this layer never did.
const throwFetchError = (error: unknown, path: `/${string}`): never => {
  if (!(error instanceof Error)) throw error;

  if (isTimeoutError(error)) throw new FetchError(`Request to ${path} timed out`, { cause: error });
  if (isAbortError(error)) throw new FetchError(`Request to ${path} aborted`, { cause: error });
  if (isFetchError(error)) throw new FetchError(`Could not reach the server at ${path}`, { cause: error });

  if (shouldCaptureErrorInSentry(error)) captureException(error, { level: 'error' });

  throw error;
};
